/**
 * ErrorFallback — the React tree rendered when Sentry's ErrorBoundary
 * catches an uncaught render-time exception in the public bundle.
 *
 * Visual language mirrors the existing LandingPage hero: brand green
 * sphere gradient, `font-century`, off-white background, and the same
 * `Probeer het opnieuw` copy idiom the checkout flow uses for retry
 * affordances (see `useConfigurator.handleSubmit`'s fallback message).
 *
 * The eventId comes from Sentry; we show only the leading 8 chars to
 * give the user something to quote without leaking a long opaque
 * identifier. Empty when Sentry is disabled (no DSN) — the boundary
 * still renders, the eventId is just absent.
 */

import React from 'react';

export interface ErrorFallbackProps {
  error: unknown;
  componentStack?: string;
  eventId?: string | null;
  resetError?: () => void;
}

export function ErrorFallback({
  error,
  eventId,
  resetError,
}: ErrorFallbackProps): React.ReactElement {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : 'Er is iets misgegaan.';

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="min-h-screen bg-[#fcfcfc] font-century flex flex-col items-center justify-center px-6 py-24 text-center relative overflow-hidden"
    >
      <div className="sphere-gradient w-[80vw] h-[80vw] max-w-96 max-h-96 -top-20 -right-[10%]" />
      <div className="sphere-gradient w-[70vw] h-[70vw] max-w-80 max-h-80 top-[10%] -left-[10%]" />

      <div className="relative max-w-xl w-full flex flex-col items-center gap-6 z-10">
        <h1 className="text-3xl md:text-4xl font-bold text-[#222]">
          Er is iets misgegaan
        </h1>
        <p className="text-base text-[#555]">
          We konden je sessie niet correct laden. Probeer het opnieuw — als het
          probleem aanhoudt, neem contact met ons op via{' '}
          <a
            href="mailto:brandbox@ddg-europe.com"
            className="text-[#356d4e] underline underline-offset-2"
          >
            brandbox@ddg-europe.com
          </a>
          .
        </p>
        {message ? (
          <p className="text-sm text-[#888] italic">{message}</p>
        ) : null}
        {eventId ? (
          <p className="text-xs text-[#999]">
            Foutreferentie: <code className="font-mono">{eventId.slice(0, 8)}</code>
          </p>
        ) : null}
        <div className="flex flex-row gap-3 mt-2">
          {resetError ? (
            <button
              type="button"
              onClick={resetError}
              className="px-5 py-2.5 rounded-md bg-[#57c858] text-white font-semibold hover:bg-[#356d4e] transition-colors"
            >
              Probeer opnieuw
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              if (typeof window !== 'undefined') window.location.reload();
            }}
            className="px-5 py-2.5 rounded-md border border-[#57c858] text-[#356d4e] font-semibold hover:bg-[#57c858]/10 transition-colors"
          >
            Pagina herladen
          </button>
        </div>
      </div>
    </div>
  );
}
